import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useAuth } from "../auth/AuthProvider";

import { getPageTitle } from "../../utils/getPageTitle";
import Button from "../../components/common/button";
import SearchItem from "../../components/searchItem";
import Pagination from "../../components/common/pagination";
import DeleteModal from "../../components/modal/deleteModal";
import EnquiryModal from "../../components/modal/enquiryModal";
import ViewEnquiryModal from "../../components/modal/viewEnquiryModal";
import EnquiryData from "../../components/enquiryData";
import { useEnquiryContext } from "../../context/EnquiryContext";

import { useViewEnquiryList } from "../../hooks/enquiries/viewEnquiryList";
import { useCreateEnquiry } from "../../hooks/enquiries/createEnquiry";
import { useEditEnquiry } from "../../hooks/enquiries/editEnquiry";
import { useDeleteEnquiry } from "../../hooks/enquiries/deleteEnquiry";
import { useUpdateEnquiryStatus } from "../../hooks/enquiries/updateEnquiryStatus";

import "../../assets/styles/products.css";
import "../../assets/styles/pagination.css";

const STATUS_OPTIONS = ["all", "pending", "closed"];

const Enquiries = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { markMultipleAsRead } = useEnquiryContext();
  const enquiryScopeKey = user?.id || user?.companyId;

  /* ── STATE ─────────────────────────────── */
  const [localEnquiries, setLocalEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [formMode, setFormMode] = useState("create"); // create | edit
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all");
  const [modalKey, setModalKey] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalDocs: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  /* ── API HOOKS ─────────────────────────── */
  const {
    data: enquiriesResponse,
    isLoading,
    isFetching,
    isError,
  } = useViewEnquiryList({
    page,
    limit: pageSize,
    q: debouncedSearch,
    scopeKey: enquiryScopeKey,
  });

  const { mutateAsync: createEnquiry, isPending: isCreating } =
    useCreateEnquiry();
  const { mutateAsync: editEnquiry, isPending: isEditing } = useEditEnquiry();
  const { mutateAsync: deleteEnquiry, isPending: isDeleting } =
    useDeleteEnquiry();
  const { mutateAsync: updateStatus } = useUpdateEnquiryStatus();

  /* ── DEBOUNCE ───────────────────────────── */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  /* ── MARK ALL PENDING ENQUIRIES AS READ WHEN DATA LOADS ── */
  useEffect(() => {
    if (
      !enquiriesResponse?.enquiries ||
      enquiriesResponse.enquiries.length === 0
    )
      return;
    const pendingIds = enquiriesResponse.enquiries
      .filter((e) => String(e.status || "").toLowerCase() === "pending")
      .map((e) => e._id || e.id);
    if (pendingIds.length > 0) {
      markMultipleAsRead(pendingIds);
    }
  }, [enquiriesResponse, markMultipleAsRead]);

  /* ── SYNC DATA ──────────────────────────── */
  useEffect(() => {
    if (!enquiriesResponse) return;
    const all = enquiriesResponse.enquiries ?? [];
    setLocalEnquiries(
      statusFilter === "all"
        ? all
        : all.filter(
            (e) => String(e.status || "").toLowerCase() === statusFilter,
          ),
    );
    const p = enquiriesResponse.pagination;
    if (p) {
      setPagination({
        currentPage: p.currentPage || 1,
        totalPages: p.totalPages || 1,
        totalDocs: p.total || 0,
        hasNextPage: p.hasNextPage || false,
        hasPrevPage: p.hasPrevPage || false,
      });
    }
  }, [enquiriesResponse, statusFilter]);

  /* ── OPEN HANDLERS ──────────────────────── */

  const openView = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setIsViewModalOpen(true);
  };

  const openEdit = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setFormMode("edit");
    setIsFormModalOpen(true);
  };

  const openDelete = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setIsDeleteModalOpen(true);
  };

  /* ── SAVE ───────────────────────────────── */
  const handleSave = async (values) => {
    try {
      if (formMode === "create") {
        setPage(1);
        await createEnquiry(values);
        toast.success("Enquiry created successfully");
      } else {
        const id = selectedEnquiry._id || selectedEnquiry.id;
        await editEnquiry({ id, body: values });
        toast.success("Enquiry updated successfully");
      }
      setIsFormModalOpen(false);
      setSelectedEnquiry(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save enquiry");
    }
  };

  /* ── DELETE ─────────────────────────────── */
  const handleConfirmDelete = async () => {
    const id = selectedEnquiry?._id || selectedEnquiry?.id;
    try {
      await deleteEnquiry(id);
      toast.success("Enquiry deleted successfully");
      setIsDeleteModalOpen(false);
      setSelectedEnquiry(null);
      queryClient.invalidateQueries({ queryKey: ["enquiryList"] });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete enquiry");
    }
  };

  /* ── STATUS CHANGE ──────────────────────── */
  const handleStatusChange = async (enquiry, newStatus) => {
    const id = enquiry._id || enquiry.id;
    try {
      await updateStatus({ id, status: newStatus });
      toast.success(`Enquiry marked as ${newStatus}`);
      queryClient.invalidateQueries({ queryKey: ["enquiryList"] });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update status");
    }
  };

  /* ── RENDER ─────────────────────────────── */
  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading enquiries</div>;

  return (
    <>
      {/* PAGE HEADER */}
      <div className="page-body">
        <div className="page-header">
          <h1 className="page-title">{getPageTitle(location.pathname)}</h1>
          <p style={{ color: "#555", fontSize: "13px" }}>
            View and manage enquiries submitted by users
          </p>
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="list-toolbar enquiries-toolbar">
        <div className="list-toolbar-row">
          <SearchItem
            data={localEnquiries}
            searchField={["name", "salesUserId.name"]}
            placeholder="Search enquiries..."
            wrapSection={false}
            onResultsChange={({ searchItem, filteredData }) => {
              setSearchTerm(searchItem);
              setFilteredEnquiries(filteredData);
            }}
          />

          <label className="list-toolbar-page-size">
            <span>Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
          </label>

          <label className="list-toolbar-page-size">
            <span>Per page:</span>
            <select
              value={pageSize}
              aria-label="Enquiries per page"
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {[5, 10, 15, 20].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {/* VIEW MODAL */}
      <ViewEnquiryModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedEnquiry(null);
        }}
        enquiry={selectedEnquiry}
      />

      {/* CREATE / EDIT MODAL */}
      <EnquiryModal
        key={
          formMode === "create"
            ? `create-${modalKey}`
            : `edit-${selectedEnquiry?._id || selectedEnquiry?.id}`
        }
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setSelectedEnquiry(null);
        }}
        enquiry={selectedEnquiry}
        mode={formMode}
        onSave={handleSave}
        isLoading={isCreating || isEditing}
      />

      {/* DELETE MODAL */}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedEnquiry(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Enquiry"
        itemName={selectedEnquiry?.subject || selectedEnquiry?.name}
        isLoading={isDeleting}
      />

      {/* TABLE */}
      <EnquiryData
        data={filteredEnquiries}
        onView={openView}
        onDelete={openDelete}
        onStatusChange={handleStatusChange}
      />

      {isFetching && !isLoading && (
        <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>Updating...</p>
      )}

      <Pagination
        currentPage={page}
        totalPages={pagination.totalPages}
        onPageChange={(p) => setPage(p)}
      />
    </>
  );
};

export default Enquiries;
